import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { LITRES_PER_GALLON } from 'utils/unitConverter/conversionFactors'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from 'utils/unitConverter/types'

type TEmitterFlowRateUnit = 'gal/h' | 'l/h'

const DEFAULT_DECIMAL_PLACES = 2
const LITRES_PER_HOUR_IN: Record<TEmitterFlowRateUnit, number> = { 'gal/h': LITRES_PER_GALLON, 'l/h': 1 }

export const emitterFlowRate: Record<
  'emitterFlowRate',
  (
    flowRate?: number | null,
    options?: TUnitConverterOptionsWithUnits<TEmitterFlowRateUnit>,
  ) => TUnitConverterTypicalReturn
> = {
  emitterFlowRate: (
    flowRate: number | null = null,
    {
      decimalPlaces = DEFAULT_DECIMAL_PLACES,
      inputUnit = 'l/h',
      outputUnit = userDetailsStore.getState().rain === 'IMPERIAL' ? 'gal/h' : 'l/h',
    }: TUnitConverterOptionsWithUnits<TEmitterFlowRateUnit> = {},
  ) => {
    const convertedValue = isNil(flowRate)
      ? null
      : (flowRate * LITRES_PER_HOUR_IN[inputUnit]) / LITRES_PER_HOUR_IN[outputUnit]

    const measurement =
      outputUnit === 'gal/h' ? translate.measurements.gallonsPerHour : translate.measurements.litersPerHour

    const unitSymbol = measurement.unit()
    const label = translate.phrases.banyanApp('Emitter flow rate')

    return {
      categoryTitle: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      categoryTitleWithoutUnit: () => label,
      defaultNumberOfDecimalPlaces: () => DEFAULT_DECIMAL_PLACES,
      suffix: () => unitSymbol,
      shortTitle: () => label,
      title: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      titleWithoutUnit: () => label,
      value: () => (isNil(convertedValue) ? null : +convertedValue.toFixed(decimalPlaces)),
      valueAsString: () => measurement.value(convertedValue, decimalPlaces),
      valueWithSuffix: () => measurement.valueWithUnit(convertedValue, decimalPlaces),
      valueWithNoRounding: () => convertedValue,
    }
  },
}
